import React, { useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import {
  ACCEPTED_ATTACHMENT_TYPES,
  AttachmentIcon,
  MAX_FILES,
  MAX_TOTAL_BYTES,
  STATUS_CONFIG,
  STATUS_KEYS,
  formatFileSize,
  friendlyErrorMessage,
  makeLocalFileId,
  namePastedFiles,
  validateAttachmentSelection,
} from "./helpers";

const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(1.5) },
  field: { marginBottom: theme.spacing(1) },
  row: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(1) },
  fileList: { marginTop: theme.spacing(0.5), display: "flex", flexDirection: "column", gap: theme.spacing(0.5) },
  fileRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), padding: theme.spacing(0.25, 1) },
  fileName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileSize: { color: theme.palette.text.secondary, fontSize: 12, whiteSpace: "nowrap" },
  rejected: { marginTop: theme.spacing(0.5), fontSize: 12, color: "#C62828" },
}));

const RELOAD_MESSAGE = "Andamento registrado, mas não foi possível atualizar a tela. Recarregue a página.";

/**
 * Formulário "Novo andamento": texto, selo opcional, anexos (botão e Ctrl+V).
 * Com arquivos envia multipart; sem arquivos, JSON. Limpa só em sucesso.
 */
const FollowUpForm = ({ occurrenceId, onSaved, onReload }) => {
  const classes = useStyles();
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [files, setFiles] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  // Espelho síncrono da lista, para validar com a soma real mesmo em colagens seguidas.
  const filesRef = useRef([]);

  const addFiles = (incoming) => {
    const current = filesRef.current;
    const bytes = current.reduce((sum, entry) => sum + entry.file.size, 0);
    const { accepted, rejected: invalid } = validateAttachmentSelection(
      incoming,
      current.length,
      bytes,
      MAX_TOTAL_BYTES
    );
    setRejected(invalid);
    if (!accepted.length) return;
    const next = [...current, ...accepted.map((file) => ({ id: makeLocalFileId(), file }))];
    filesRef.current = next;
    setFiles(next);
  };

  const removeFile = (id) => {
    const next = filesRef.current.filter((entry) => entry.id !== id);
    filesRef.current = next;
    setFiles(next);
    setRejected([]);
  };

  // Mesmo padrão do NewOccurrenceModal: só intercepta quando há arquivo na área de transferência.
  const handlePaste = (event) => {
    if (event.clipboardData?.files?.length > 0) {
      event.preventDefault();
      addFiles(namePastedFiles(Array.from(event.clipboardData.files)));
    }
  };

  const canSend = !saving && (Boolean(text.trim()) || files.length > 0);

  const handleSend = async () => {
    if (!canSend) return;
    setSaving(true);
    try {
      let response;
      if (files.length > 0) {
        const formData = new FormData();
        formData.append("text", text.trim());
        if (status) formData.append("status", status);
        files.forEach((entry) => formData.append("files", entry.file));
        response = await api.post(`/ocorrencias/${encodeURIComponent(occurrenceId)}/andamentos`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const body = { text: text.trim() };
        if (status) body.status = status;
        response = await api.post(`/ocorrencias/${encodeURIComponent(occurrenceId)}/andamentos`, body);
      }
      setText("");
      setStatus("");
      filesRef.current = [];
      setFiles([]);
      setRejected([]);
      onSaved(response.data);
    } catch (err) {
      const httpStatus = err.response && err.response.status;
      const serverMessage = err.response && err.response.data && err.response.data.error;
      if (httpStatus === 500 && serverMessage === RELOAD_MESSAGE) {
        toast.error(RELOAD_MESSAGE);
        setText("");
        setStatus("");
        filesRef.current = [];
        setFiles([]);
        if (onReload) onReload();
      } else {
        const friendly = friendlyErrorMessage(err);
        if (friendly) toast.error(friendly);
        else toastError(err);
      }
    }
    setSaving(false);
  };

  return (
    <div className={classes.root}>
      <TextField
        className={classes.field}
        label="Novo andamento"
        placeholder="Descreva o andamento. Você pode colar uma imagem (Ctrl+V)."
        fullWidth
        multiline
        minRows={2}
        variant="outlined"
        size="small"
        value={text}
        disabled={saving}
        onChange={(event) => setText(event.target.value)}
        onPaste={handlePaste}
      />

      <div className={classes.row}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel id={`followup-status-${occurrenceId}`}>Mudar selo para</InputLabel>
          <Select
            labelId={`followup-status-${occurrenceId}`}
            label="Mudar selo para"
            value={status}
            displayEmpty
            disabled={saving}
            onChange={(event) => setStatus(event.target.value)}
          >
            <MenuItem value="">Não mudar</MenuItem>
            {STATUS_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {STATUS_CONFIG[key].label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={Object.values(ACCEPTED_ATTACHMENT_TYPES).flat().join(",")}
          onChange={(event) => {
            addFiles(Array.from(event.target.files || []));
            event.target.value = "";
          }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AttachFileIcon fontSize="small" />}
          disabled={saving}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          Anexar
        </Button>
        <Typography variant="caption" color="textSecondary">
          até {MAX_FILES} arquivos, 25 MB cada, 24 MB no total
        </Typography>

        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!canSend}
          onClick={handleSend}
          style={{ marginLeft: "auto" }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Registrar andamento"}
        </Button>
      </div>

      {rejected.length > 0 ? (
        <div className={classes.rejected}>
          {rejected.map((item, index) => (
            <div key={`${item.name}-${index}`}>
              {item.name} — {item.reason}
            </div>
          ))}
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className={classes.fileList}>
          {files.map((entry) => (
            <Paper key={entry.id} variant="outlined" className={classes.fileRow}>
              <AttachmentIcon mimeType={entry.file.type} fontSize="small" color="action" />
              <Typography variant="body2" className={classes.fileName} title={entry.file.name}>
                {entry.file.name}
              </Typography>
              <span className={classes.fileSize}>{formatFileSize(entry.file.size)}</span>
              <IconButton size="small" disabled={saving} onClick={() => removeFile(entry.id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default FollowUpForm;
